if (!customElements.get('scene-cards')) {
  customElements.define(
    'scene-cards',
    class SceneCards extends HTMLElement {
      connectedCallback() {
        this.list = this.querySelector('[data-scene-list]');
        this.cards = Array.from(this.querySelectorAll('[data-scene-card]'));
        this.previousButton = this.querySelector('[data-scene-previous]');
        this.nextButton = this.querySelector('[data-scene-next]');
        this.dots = Array.from(this.querySelectorAll('[data-scene-dot]'));

        if (!this.list || this.cards.length <= 3 || this.initialized) return;

        this.initialized = true;
        this.rafId = null;
        this.abortController = new AbortController();
        const { signal } = this.abortController;

        this.previousButton?.addEventListener('click', () => this.goTo(this.activeIndex - 1), { signal });
        this.nextButton?.addEventListener('click', () => this.goTo(this.activeIndex + 1), { signal });
        this.dots.forEach((dot, index) => {
          dot.addEventListener('click', () => this.goTo(index), { signal });
        });
        this.cards.forEach((card, index) => {
          card.addEventListener('click', (event) => {
            const link = event.target.closest('a');
            if (link && index === this.activeIndex) return;
            if (link) event.preventDefault();
            this.goTo(index);
          }, { signal });

          card.addEventListener('keydown', (event) => {
            if (event.key !== 'Enter' && event.key !== ' ') return;
            if (event.target.closest('a')) return;
            event.preventDefault();
            this.goTo(index);
          }, { signal });
        });

        this.addEventListener('shopify:block:select', (event) => {
          const card = event.target.closest?.('[data-scene-card]');
          const index = this.cards.indexOf(card);
          if (index >= 0) this.goTo(index, 'auto');
        }, { signal });

        this.list.addEventListener('scroll', () => this.requestUpdate(), { passive: true, signal });
        window.addEventListener('resize', () => this.requestUpdate(), { passive: true, signal });
        this.update();
      }

      disconnectedCallback() {
        this.abortController?.abort();
        if (this.rafId) cancelAnimationFrame(this.rafId);
        this.initialized = false;
      }

      requestUpdate() {
        if (this.rafId) return;
        this.rafId = requestAnimationFrame(() => {
          this.rafId = null;
          this.update();
        });
      }

      get activeIndex() {
        if (!this.list || !this.cards.length) return 0;

        const listLeft = this.list.getBoundingClientRect().left;
        let closestIndex = 0;
        let closestDistance = Number.POSITIVE_INFINITY;

        this.cards.forEach((card, index) => {
          const distance = Math.abs(card.getBoundingClientRect().left - listLeft);
          if (distance < closestDistance) {
            closestDistance = distance;
            closestIndex = index;
          }
        });

        return closestIndex;
      }

      goTo(index, behavior) {
        const targetIndex = Math.max(0, Math.min(index, this.cards.length - 1));
        const target = this.cards[targetIndex];
        if (!target) return;

        this.list.scrollTo({
          left: target.offsetLeft - this.list.offsetLeft,
          behavior: behavior || (window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'),
        });
      }

      update() {
        const index = this.activeIndex;
        const atStart = this.list.scrollLeft <= 2;
        const atEnd = this.list.scrollLeft + this.list.clientWidth >= this.list.scrollWidth - 2;

        if (this.previousButton) this.previousButton.disabled = atStart;
        if (this.nextButton) this.nextButton.disabled = atEnd;

        this.cards.forEach((card, cardIndex) => {
          const active = cardIndex === index;
          card.classList.toggle('is-selected', active);
          card.setAttribute('aria-current', active ? 'true' : 'false');
        });

        this.dots.forEach((dot, dotIndex) => {
          const active = dotIndex === index;
          dot.classList.toggle('is-active', active);
          dot.setAttribute('aria-current', active ? 'true' : 'false');
        });
      }
    }
  );
}
